import { createAsyncThunk } from '@reduxjs/toolkit';
import { getPrivateThreadThunk } from './getPrivateThread';
import { getPrivateThreadsThunk } from './getPrivateThreads';
import { setIsRecording } from '../slices/videoChatSlice';
import { typingReceived, TYPING_TTL_MS } from '../slices/chatTypingSlice';

// Threads of calls ringing here whose fetch is under way.
const callThreadsBeingFetched = new Set();

export const handleWebsocketMessageThunk = createAsyncThunk(
  'chat/handleWebsocketMessage',
  async ({ event, user }, { getState, dispatch }) => {
    const newMessage = JSON.parse(event.data);
    const messageType = newMessage?.type?.split('#')[0];

    let threadWasFetched = false;

    // If this is a MESSAGE from a direct recipient and we don't have the thread in state, fetch it
    if (
      messageType === 'MESSAGE' &&
      newMessage.directRecipient &&
      newMessage.userId !== user?.id
    ) {
      const state = getState();
      const existingThread = state.chatSlice.threads[newMessage.threadId];

      if (!existingThread) {
        // Fetch the private thread so it's added to state
        // The thread from backend already includes the unread count for this message
        // setAsActive: false prevents this from becoming the active thread
        await dispatch(
          getPrivateThreadThunk({
            userId: newMessage.userId,
            setAsActive: false,
            projectId: newMessage.projectId,
          })
        );
        threadWasFetched = true;
      }
    }

    // A 1:1 call ringing on a thread this tab has never seen - the first call
    // between two people creates the thread. Answering opens the thread from
    // state, so fetch it; the session row names no project, which
    // get-private-thread needs, so all of the user's private threads come.
    // Not waited for: the ring must not wait on it. The session is sent again
    // as it changes, so one fetch per thread at a time.
    if (
      messageType === 'VIDEOCHAT_SESSION' &&
      newMessage.isActive &&
      newMessage.directRecipient === user?.id &&
      newMessage.userId !== user?.id &&
      !getState().chatSlice.threads[newMessage.threadId] &&
      !callThreadsBeingFetched.has(newMessage.threadId)
    ) {
      callThreadsBeingFetched.add(newMessage.threadId);
      dispatch(getPrivateThreadsThunk()).finally(() =>
        callThreadsBeingFetched.delete(newMessage.threadId)
      );
    }

    // Handle recording start/stop notifications
    if (messageType === 'VIDEOCHAT_RECORDING') {
      dispatch(setIsRecording(newMessage.isActive === true));
    }

    // Someone is writing in a thread. Transient, so it lives in its own slice
    // rather than in chatSlice — see the note there. The sender's own ping
    // comes back too (the fan-out has no self-exclusion), and showing yourself
    // as typing would be nonsense, so drop it here.
    if (messageType === 'TYPING' && newMessage.userId !== user?.id) {
      dispatch(
        typingReceived({
          threadId: newMessage.threadId,
          userId: newMessage.userId,
          isTyping: newMessage.isTyping !== false,
          expiresAt: Date.now() + TYPING_TTL_MS,
        })
      );
    }

    return { message: newMessage, threadWasFetched };
  }
);
